import { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { cleanTrip, getGuideTrips, getGuideTripsSearch } from "store/trips"

const MIN_SEARCH_CHARS = 3
const SORT_BY = "updated_at_desc"

export const useExperienceListingsFetch = ({
  activeListingTab,
  page,
  pageSize,
  metadata,
  searchQuery,
  confirmedSearchQuery,
  debouncedSearchQuery,
  selectedTrip,
  buildFilterQuery
}) => {
  const dispatch = useDispatch()
  const [originalStatusCount, setOriginalStatusCount] = useState(null)

  // Preserve original status_count before search API overwrites it
  useEffect(() => {
    if (metadata?.status_count && !searchQuery.trim()) {
      setOriginalStatusCount(metadata.status_count)
    }
  }, [metadata?.status_count, searchQuery])

  // Fetch by tab + pagination
  useEffect(() => {
    if (!activeListingTab) return undefined
    if (confirmedSearchQuery.trim() || selectedTrip) return undefined

    dispatch(
      getGuideTrips(null, {
        q: { status_eq: activeListingTab, ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: SORT_BY
      })
    )

    return () => {
      dispatch(cleanTrip())
    }
  }, [dispatch, activeListingTab, page, pageSize, confirmedSearchQuery, selectedTrip, buildFilterQuery])

  // Fetch suggestions while typing
  useEffect(() => {
    if (confirmedSearchQuery.trim()) return
    if (debouncedSearchQuery.trim().length < MIN_SEARCH_CHARS || selectedTrip) return

    dispatch(
      getGuideTripsSearch(null, {
        q: { title_cont: debouncedSearchQuery.trim(), ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: SORT_BY,
        search: true
      })
    )
  }, [debouncedSearchQuery, dispatch, page, pageSize, confirmedSearchQuery, selectedTrip, buildFilterQuery])

  // Fetch paginated search results after Enter / select
  useEffect(() => {
    if (!confirmedSearchQuery.trim() || selectedTrip) return

    dispatch(
      getGuideTripsSearch(null, {
        q: { title_cont: confirmedSearchQuery.trim(), ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: SORT_BY,
        search: false
      })
    )
  }, [confirmedSearchQuery, page, pageSize, dispatch, selectedTrip, buildFilterQuery])

  const refetchCurrent = useCallback(() => {
    dispatch(
      getGuideTrips(null, {
        q: { status_eq: activeListingTab, ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: SORT_BY
      })
    )
  }, [dispatch, activeListingTab, page, pageSize, buildFilterQuery])

  const fetchTripByTitle = useCallback(
    (trip) => {
      dispatch(
        getGuideTripsSearch(null, {
          q: { status_eq: trip.status, title_eq: trip.title },
          page: 1,
          per_page: pageSize,
          sort_by: SORT_BY,
          search: false
        })
      )
    },
    [dispatch, pageSize]
  )

  return { originalStatusCount, refetchCurrent, fetchTripByTitle }
}
